import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { appointmentsAPI } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, confirmAction } from '../utils/helpers'
import CustomSelect from '../components/common/CustomSelect'

const Appointments = () => {
  const { showSuccess, showError } = useNotification()
  const [searchParams, setSearchParams] = useSearchParams()
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const filter = searchParams.get('status') || 'all'

  useEffect(() => {
    fetchAppointments()
  }, [filter])

  const fetchAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll(filter)
      setAppointments(response.data.appointments || [])
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (id, status) => {
    try {
      await appointmentsAPI.updateStatus(id, status)
      showSuccess('Appointment status updated.')
      fetchAppointments()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleDelete = async (id) => {
    if (!confirmAction('Delete this appointment?')) return
    
    try {
      await appointmentsAPI.delete(id)
      showSuccess('Appointment deleted.')
      fetchAppointments()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const setFilter = (newFilter) => {
    setSearchParams({ status: newFilter })
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">Appointments</h2>
      <p className="admin-sub">Manage customer appointment requests</p>

      <div className="panel">
        <div className="panel-head">
          <span>Filter by status</span>
          <div className="panel-actions">
            <button onClick={() => setFilter('all')} className={`btn btn-sm btn-outline-dark ${filter === 'all' ? 'active' : ''}`}>All</button>
            <button onClick={() => setFilter('pending')} className={`btn btn-sm btn-outline-dark ${filter === 'pending' ? 'active' : ''}`}>Pending</button>
            <button onClick={() => setFilter('confirmed')} className={`btn btn-sm btn-outline-dark ${filter === 'confirmed' ? 'active' : ''}`}>Confirmed</button>
            <button onClick={() => setFilter('completed')} className={`btn btn-sm btn-outline-dark ${filter === 'completed' ? 'active' : ''}`}>Completed</button>
            <button onClick={() => setFilter('cancelled')} className={`btn btn-sm btn-outline-dark ${filter === 'cancelled' ? 'active' : ''}`}>Cancelled</button>
          </div>
        </div>

        {appointments.length > 0 ? (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Service</th>
                  <th>Date / Time</th>
                  <th>Price Range</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((apt) => (
                  <tr key={apt.id}>
                    <td>{apt.id}</td>
                    <td>{apt.name}</td>
                    <td>{apt.phone}</td>
                    <td>{apt.service || '—'}</td>
                    <td>{apt.appointment_date || '—'} {apt.appointment_time || ''}</td>
                    <td>{apt.price_range || '—'}</td>
                    <td>
                      <CustomSelect
                        value={apt.status}
                        onChange={(val) => handleStatusChange(apt.id, val)}
                        small
                        options={[
                          { value: 'pending',   label: 'Pending' },
                          { value: 'confirmed', label: 'Confirmed' },
                          { value: 'completed', label: 'Completed' },
                          { value: 'cancelled', label: 'Cancelled' },
                        ]}
                      />
                    </td>
                    <td className="actions-cell">
                      <button onClick={() => handleDelete(apt.id)} className="btn btn-sm btn-danger">
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No appointments found.</p>
        )}
      </div>
    </div>
  )
}

export default Appointments
